<script setup lang="ts">
import { ref, onUnmounted, watch, nextTick } from 'vue';

interface Props {
  target?: HTMLElement;
  visible: boolean;
  text: string;
  position?: 'top' | 'right' | 'bottom' | 'left' | 'auto';
  offset?: number;
  maxWidth?: string;
  minWidth?: string;
  width?: string;
  maxHeight?: string;
  minHeight?: string;
  height?: string;
  multiline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top',
  offset: 8,
  maxWidth: '300px',
  minWidth: 'auto',
  width: undefined,
  maxHeight: undefined,
  minHeight: undefined,
  height: undefined,
  multiline: true
});

const tooltipRef = ref<HTMLElement>();
const tooltipStyle = ref({});
// resolved position used by the arrow (may differ from props.position when 'auto')
const resolvedPosition = ref<'top' | 'right' | 'bottom' | 'left'>('top');

/**
 * When position === 'auto', pick the side that has the most available space
 * and that can fully contain the tooltip without clipping.
 */
const resolveAutoPosition = (
  targetRect: DOMRect,
  tooltipRect: DOMRect,
  offset: number
): 'top' | 'right' | 'bottom' | 'left' => {
  const { innerWidth: vw, innerHeight: vh } = window;

  const space = {
    top:    targetRect.top - offset,
    bottom: vh - targetRect.bottom - offset,
    left:   targetRect.left - offset,
    right:  vw - targetRect.right - offset,
  };

  const fits = {
    top:    space.top    >= tooltipRect.height,
    bottom: space.bottom >= tooltipRect.height,
    left:   space.left   >= tooltipRect.width,
    right:  space.right  >= tooltipRect.width,
  };

  // Prefer top → bottom → right → left (common UX convention)
  if (fits.top)    return 'top';
  if (fits.bottom) return 'bottom';
  if (fits.right)  return 'right';
  if (fits.left)   return 'left';

  // Nothing fits perfectly — fall back to the side with most space
  return (Object.keys(space) as Array<'top' | 'bottom' | 'left' | 'right'>)
    .reduce((a, b) => space[a] >= space[b] ? a : b);
};

const updatePosition = () => {
  if (!props.target || !props.visible || !tooltipRef.value) return;

  const targetRect  = props.target.getBoundingClientRect();
  const tooltipRect = tooltipRef.value.getBoundingClientRect();
  const { innerWidth: vw, innerHeight: vh } = window;

  // Determine which side to place the tooltip on
  const side: 'top' | 'right' | 'bottom' | 'left' =
    props.position === 'auto'
      ? resolveAutoPosition(targetRect, tooltipRect, props.offset)
      : props.position;

  resolvedPosition.value = side;

  let left = 0;
  let top  = 0;
  let transform = '';

  switch (side) {
    case 'top':
      left      = targetRect.left + targetRect.width / 2;
      top       = targetRect.top - props.offset;
      transform = 'translateX(-50%) translateY(-100%)';
      // Clamp horizontally
      left = Math.max(tooltipRect.width / 2 + 10,
               Math.min(left, vw - tooltipRect.width / 2 - 10));
      break;

    case 'bottom':
      left      = targetRect.left + targetRect.width / 2;
      top       = targetRect.bottom + props.offset;
      transform = 'translateX(-50%)';
      // Clamp horizontally
      left = Math.max(tooltipRect.width / 2 + 10,
               Math.min(left, vw - tooltipRect.width / 2 - 10));
      break;

    case 'left':
      left      = targetRect.left - props.offset;
      top       = targetRect.top + targetRect.height / 2;
      transform = 'translateX(-100%) translateY(-50%)';
      // If no room on the left, flip to right (only when position is explicitly 'left')
      if (props.position !== 'auto' && left - tooltipRect.width < 0) {
        left      = targetRect.right + props.offset;
        transform = 'translateY(-50%)';
        resolvedPosition.value = 'right';
      }
      // Clamp vertically
      top = Math.max(tooltipRect.height / 2 + 10,
              Math.min(top, vh - tooltipRect.height / 2 - 10));
      break;

    case 'right':
      left      = targetRect.right + props.offset;
      top       = targetRect.top + targetRect.height / 2;
      transform = 'translateY(-50%)';
      // If no room on the right, flip to left (only when position is explicitly 'right')
      if (props.position !== 'auto' && left + tooltipRect.width > vw) {
        left      = targetRect.left - props.offset;
        transform = 'translateX(-100%) translateY(-50%)';
        resolvedPosition.value = 'left';
      }
      // Clamp vertically
      top = Math.max(tooltipRect.height / 2 + 10,
              Math.min(top, vh - tooltipRect.height / 2 - 10));
      break;
  }

  tooltipStyle.value = {
    position:  'fixed',
    left:      `${left}px`,
    top:       `${top}px`,
    transform,
    zIndex:    '99999',
    maxWidth:  props.maxWidth,
    minWidth:  props.minWidth,
    width:     props.width,
    maxHeight:  props.maxHeight,
    minHeight:  props.minHeight,
    height:     props.height,
  };
};

const handleScroll = () => updatePosition();
const handleResize = () => updatePosition();

watch(() => props.visible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    });
  } else {
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleResize);
  }
}, { immediate: true });

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <teleport to="body">
    <div
      v-if="visible"
      ref="tooltipRef"
      :style="tooltipStyle"
      class="fixed px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none z-[99999]"
      :class="{
        'whitespace-normal break-words': multiline,
        'whitespace-nowrap': !multiline,
        'text-center': resolvedPosition === 'top' || resolvedPosition === 'bottom'
      }"
    >
      {{ text }}

      <!-- Tooltip arrow — driven by resolvedPosition so auto works correctly -->
      <div
        v-if="resolvedPosition === 'top'"
        class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"
      />
      <div
        v-if="resolvedPosition === 'bottom'"
        class="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900"
      />
      <div
        v-if="resolvedPosition === 'left'"
        class="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-gray-900"
      />
      <div
        v-if="resolvedPosition === 'right'"
        class="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"
      />
    </div>
  </teleport>
</template>
